/*
 * Created on 02.05.2017.
 *
 * Copyright(c) 1995 - 2017
 * T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */
import { AfterContentInit, Component, ContentChild, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LoggingService } from '../../utils/logging.service';

/**
 * Komponente die ControlValueAccessor wrapped um eine Datentransformation zu ermöglichen.
 * Die Input-Parameter 'fromModel' und 'toModel' transformieren die Daten die in den gewrappten
 * ControlValueAccessor gehen bzw. aus dem ControlValueAccessor kommen.
 *
 * Siehe auch http://stackoverflow.com/a/43561718
 *
 * @author Valentin Kennke (vake@mms-dresden.de)(valentin.kennke@t-systems.com)
 */
@Component({
    selector: 'common-control-value-mapper',
    template: '<ng-content></ng-content>',
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => ControlValueMapperComponent),
        multi: true
    }]
})
export class ControlValueMapperComponent implements ControlValueAccessor, AfterContentInit {
    @ContentChild('cvDelegate', { static: true })
    delegate: ControlValueAccessor;

    @Input()
    fromModel: (_) => any;

    @Input()
    toModel: (_) => any;

    /** Sagt, ob die Angular-Lifecycle-Hook AfterContentInit aufgerufen wurde. */
    afterContentInitHookDone = false;

    /**
     * Hält eventuell einen Wert, der an {@link #writeValue()} übergeben wurde, bevor
     * {@link #afterContentInitHookDone} true ist.
     */
    maybeCachedWriteValue: any | null = null;

    constructor(private log: LoggingService) {
    }

    setDisabledState(isDisabled: boolean) {
        this.delegate.setDisabledState(isDisabled);
    }

    ngAfterContentInit(): void {
        this.afterContentInitHookDone = true;
        if (this.maybeCachedWriteValue) {
            this.writeValue(this.maybeCachedWriteValue);
        }
    }

    writeValue(obj: any) {
        if (!this.afterContentInitHookDone) {
            // müssen verhindern, dass writeValue() aufgerufen wird,
            // bevor die innere Componente initialisiert wurde
            // https://jira.t-systems-mms.eu/browse/SPP-1349
            this.maybeCachedWriteValue = obj;
        } else {
            this.log.debug(`ControlValueMapperComponent#writeValue (bevor fromModel()):`);
            this.log.debug(obj);
            this.delegate.writeValue(this.fromModel(obj));
        }
    }

    registerOnChange(fn: any) {
        this.delegate.registerOnChange(value => fn(this.toModel(value)));
    }

    registerOnTouched(fn: any) {
        this.delegate.registerOnTouched(value => fn(this.toModel(value)));
    }
}
